/**
 * Public Pricing page — /pricing.
 *
 * Presentation + copy refresh (Oct 2026) aligned to the approved VOX pricing
 * prototype. This is a CUSTOMER-FACING change only:
 *   - Product names, prices, currencies, regional pricing, Stripe Price IDs,
 *     checkout handlers, subscription calculations and entitlements are all
 *     unchanged.
 *   - The plan catalog (prices + Stripe Price IDs + display names) is still
 *     sourced from GET /api/plans (reads `db.plans`).
 *   - The purchase funnel is unchanged: Get Started → signup/login → email
 *     validation → OrderReviewModal → /api/subscriptions/checkout → Stripe.
 *
 * Copy honesty note: the live-survey allowance and question limits shown here
 * mirror the ACTUAL backend enforcement (core.py PLAN_CAPABILITIES +
 * survey_builder.py PLAN_QUESTION_LIMITS):
 *   NPS® 2 questions / 1 live survey · CSAT up to 10 questions / 1 live survey
 *   VOC up to 25 questions / 2 live surveys · VOE up to 25 questions / 2 live.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import SignupModal from '@/components/SignupModal';
import OrderReviewModal from '@/components/subscription/OrderReviewModal';
import { Button } from '@/components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Check, Loader2, Star, Radio, ListChecks, PlusCircle } from 'lucide-react';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png';

// Contact destination for all contact CTAs.
const CONTACT_HREF = 'mailto:hello@satisfind.com';

// Phase 1: four VOX Store products. VOC + VOE share the same VOX Stripe
// Price IDs (their plan docs point at the same monthly/annual Stripe
// price IDs) but are distinct customer-facing products.
const VISIBLE_PLAN_IDS = ['nps_pro', 'csat', 'voc', 'voe'];

// Which plan_id gets the "MOST POPULAR" ribbon.
const FEATURED_PLAN_ID = 'csat';

// plan_id → product enum sent to /api/subscriptions/checkout.
const PLAN_TO_PRODUCT = { nps_pro: 'nps', csat: 'csat', voc: 'voc', voe: 'voe' };

const PENDING_INTENT_KEY = 'pending_subscription_intent';

// Public ROW (Rest-of-World) price table shown on the marketing /pricing
// page. Display-only — the actual charge is still computed at checkout
// after the buyer confirms their registered country (can switch to
// SEA/India pricing per the existing regional pricing logic).
const ROW_DISPLAY_PRICES = {
  nps_pro: { monthly: 18, annual: 180 },
  csat:    { monthly: 24, annual: 240 },
  voc:     { monthly: 70, annual: 700 },
  voe:     { monthly: 70, annual: 700 },
};

// Customer-facing copy per product (from the approved prototype). Live-survey
// counts and question limits reflect ACTUAL backend enforcement.
const PRODUCT_COPY = {
  nps_pro: {
    description: 'Understand how likely customers are to recommend your business.',
    liveSurvey: { main: '1 survey live at a time', sub: 'Across all your subscribed branches' },
    questions: { main: '2 questions', sub: '1 NPS\u00AE question + 1 qualifying question' },
    inclusions: [
      'Unlimited responses',
      'Measure willingness to recommend',
      'Understand the reasons behind scores',
      'Compare results across branches',
      'Download results as a spreadsheet',
    ],
  },
  csat: {
    description: 'Find out how satisfied customers are with the service they receive.',
    liveSurvey: { main: '1 survey live at a time', sub: 'Across all your subscribed branches' },
    questions: { main: 'Up to 10 questions', sub: 'NPS\u00AE and CSAT questions combined' },
    inclusions: [
      'Unlimited responses',
      'Measure satisfaction with your service',
      'Identify strengths and areas to improve',
      'View results by branch and department',
      'Download results as a spreadsheet',
    ],
  },
  voc: {
    description: 'Hear what customers experienced, what matters to them and where service can improve.',
    liveSurvey: { main: '2 surveys live at a time', sub: 'Across all your subscribed branches' },
    questions: { main: 'Up to 25 questions', sub: 'Per survey' },
    inclusions: [
      'Unlimited responses',
      'Collect text and voice feedback',
      'Read voice feedback as text',
      'Identify feedback themes and sentiment',
      'Track TrustX\u00AE scores',
    ],
  },
  voe: {
    description: 'Hear how employees experience their workplace and where support is needed.',
    liveSurvey: { main: '2 surveys live at a time', sub: 'Across all your subscribed branches' },
    questions: { main: 'Up to 25 questions', sub: 'Per survey' },
    inclusions: [
      'Unlimited responses',
      'Collect text and voice feedback from employees',
      'Read voice feedback as text',
      'Understand workplace culture and engagement',
      'Identify employee concerns and feedback themes',
    ],
  },
};

const FAQ_ITEMS = [
  {
    q: 'How many surveys can I have live at once?',
    a: 'Each NPS\u00AE and CSAT subscription includes one live survey across all your subscribed branches, and Voice of Customer and Voice of Employee each include two \u2014 whether you have 5 branches or 5,000. Adding branches does not increase this allowance. Additional live surveys are available for US$150 per survey per year for the organization.',
  },
  {
    q: 'How many questions can I include in a survey?',
    a: 'NPS\u00AE includes 2 questions: one recommendation question and one qualifying question. CSAT includes up to 10 questions. Voice of Customer and Voice of Employee each allow up to 25 questions per survey. Unlimited responses does not mean unlimited questions.',
  },
  {
    q: 'What is a live survey?',
    a: 'A live survey is a survey that is open to receive responses.',
  },
  {
    q: 'Can I purchase additional live surveys?',
    a: 'Yes. Each additional live survey costs US$150 per year for the whole organization. This additional survey fee is not charged separately for each branch. Your base product subscriptions remain priced per branch.',
  },
  {
    q: 'How does branch pricing work?',
    a: 'You need to subscribe for every branch that uses the product. Pricing is per branch. For example, if five branches use CSAT, you pay for five branches. That CSAT subscription still includes its live survey allowance across those branches.',
  },
  {
    q: 'Is there a limit on responses?',
    a: 'Each subscription includes unlimited responses. The live survey allowance determines how many surveys can receive responses at the same time. Question limits still apply to each survey.',
  },
  {
    q: 'Can I use more than one product?',
    a: 'Yes. You can subscribe to more than one product and manage them under one account. Each product subscription includes its own live survey allowance across all your subscribed branches.',
  },
  // Retained existing FAQs (answers unchanged).
  {
    q: 'Can I change my plan at any time?',
    a: 'Yes. You can upgrade at any time and your new plan takes effect immediately. Downgrades take effect at the end of your billing cycle.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted in transit and at rest. We never share your data with third parties.',
  },
  {
    q: 'Do you offer discounts for multiple branches?',
    a: 'Yes, we offer custom pricing for large deployments. Contact us at hello@satisfind.com to discuss your needs.',
  },
];

function PriceLine({ plan, billing }) {
  const row = ROW_DISPLAY_PRICES[plan.plan_id];
  if (!row) {
    return (
      <div className="min-h-[88px]" data-testid={`price-${plan.plan_id}`}>
        <p className="text-xs uppercase tracking-wider text-[#64748B]">Per branch</p>
        <p className="text-sm text-[#0F172A] mt-1">
          Priced per branch · {billing === 'annual' ? 'annual' : 'monthly'} billing.
        </p>
        <p className="text-[11px] text-[#94A3B8] mt-1">Final price is set at checkout based on where your company is registered.</p>
      </div>
    );
  }
  const amount = billing === 'annual' ? row.annual : row.monthly;
  const cadence = billing === 'annual' ? '/year' : '/month';
  return (
    <div className="min-h-[88px]" data-testid={`price-${plan.plan_id}`}>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-bold text-[#0F172A] font-['Montserrat']">
          US${amount}
        </span>
        <span className="text-sm text-[#64748B] font-medium">{cadence}</span>
      </div>
      <p className="text-xs uppercase tracking-wider text-[#64748B] mt-1">Per branch</p>
      <p className="text-[11px] text-[#94A3B8] mt-1">
        Final price is set at checkout based on where your company is registered.
      </p>
    </div>
  );
}

function AllowanceBox({ icon: Icon, main, sub, testid }) {
  return (
    <div
      className="flex items-start gap-3 rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] px-3.5 py-3"
      data-testid={testid}
    >
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#1A3E6F] text-white">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#0F172A] leading-snug">{main}</p>
        <p className="text-xs text-[#64748B] leading-snug mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState('monthly');
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupPlanId, setSignupPlanId] = useState('nps_free');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewPlan, setReviewPlan] = useState(null);
  const [reviewProduct, setReviewProduct] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/plans`);
        const data = await res.json();
        if (!cancelled && Array.isArray(data.plans)) setPlans(data.plans);
      } catch {
        /* leave empty list, page still renders */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter to only the paid plans we want to show on the marketing page,
  // and order them per VISIBLE_PLAN_IDS.
  const visiblePlans = useMemo(() => {
    const byId = Object.fromEntries(plans.map(p => [p.plan_id, p]));
    return VISIBLE_PLAN_IDS.map(id => byId[id]).filter(Boolean);
  }, [plans]);

  const openSignup = (planId = 'nps_free') => {
    setSignupPlanId(planId);
    setSignupOpen(true);
  };

  const startCheckout = async (plan) => {
    const product = PLAN_TO_PRODUCT[plan.plan_id];
    if (!product) { openSignup('nps_free'); return; }
    if (!isAuthenticated) {
      // Stash the buyer's intent so we can resume the Order Review modal
      // after they finish signup + email verification + first login.
      try {
        localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify({
          plan_id: plan.plan_id,
          product,
          billing_cycle: billing,
          expires_at: Date.now() + 86400000,
        }));
      } catch { /* localStorage may be blocked */ }
      openSignup(plan.plan_id);
      return;
    }
    setReviewPlan(plan);
    setReviewProduct(product);
    setReviewOpen(true);
  };

  // Resume the pending purchase after signup+verify+login.
  useEffect(() => {
    if (!isAuthenticated || plans.length === 0) return;
    let intent = null;
    try {
      const raw = localStorage.getItem(PENDING_INTENT_KEY);
      if (raw) intent = JSON.parse(raw);
    } catch { /* ignore */ }
    if (!intent || !intent.plan_id) return;
    if (intent.expires_at && intent.expires_at < Date.now()) {
      try { localStorage.removeItem(PENDING_INTENT_KEY); } catch { /* ignore */ }
      return;
    }
    const plan = plans.find(p => p.plan_id === intent.plan_id);
    if (!plan) return;
    try { localStorage.removeItem(PENDING_INTENT_KEY); } catch { /* ignore */ }
    setBilling(intent.billing_cycle || 'monthly');
    setReviewPlan(plan);
    setReviewProduct(intent.product || PLAN_TO_PRODUCT[intent.plan_id]);
    setReviewOpen(true);
  }, [isAuthenticated, plans]);

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SATISFIND_LOGO} alt="satisFIND" style={{ width: 160, height: 'auto' }} />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/auth" className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">Log in</Link>
            <Button
              onClick={() => openSignup('nps_free')}
              className="bg-[#1A3E6F] hover:bg-[#122c50] text-white transition-colors"
              data-testid="header-signup-btn"
            >
              Choose your product
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Montserrat'] text-[#0F172A] tracking-tight">
          Choose the right product for your business.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#64748B] max-w-2xl mx-auto">
          Understand customer loyalty, satisfaction and experiences, or hear from your employees. Subscription pricing is based on the number of branches you subscribe for.
        </p>
        <div
          className="mt-8 inline-flex items-center bg-[#F1F5F9] rounded-full p-1"
          role="tablist"
          data-testid="billing-toggle"
        >
          {['monthly', 'annual'].map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBilling(b)}
              data-testid={`billing-toggle-${b}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                billing === b ? 'bg-[#1A3E6F] text-white' : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {b === 'monthly' ? 'Monthly' : 'Annual'}
            </button>
          ))}
          <span className="ml-2 mr-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#92400E]">
            Save up to 17%
          </span>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="max-w-6xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="flex justify-center py-16" data-testid="plans-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A3E6F]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" data-testid="plans-grid">
            {visiblePlans.map((plan) => {
              const featured = plan.plan_id === FEATURED_PLAN_ID;
              const copy = PRODUCT_COPY[plan.plan_id] || {};
              return (
                <div
                  key={plan.plan_id}
                  data-testid={`plan-card-${plan.plan_id}`}
                  className={`relative rounded-2xl bg-white p-7 flex flex-col h-full transition-shadow hover:shadow-md ${
                    featured ? 'border-2 border-[#1A3E6F] shadow-lg' : 'border border-[#E2E8F0] shadow-sm'
                  }`}
                >
                  {featured && (
                    <span
                      className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#DC2626] text-white text-[11px] font-bold tracking-wide"
                      data-testid="most-popular-badge"
                    >
                      <Star className="w-3 h-3 fill-white" /> MOST POPULAR
                    </span>
                  )}
                  <h3 className="text-xl font-bold font-['Montserrat'] text-[#0F172A]">{plan.name}</h3>
                  <p className="text-sm text-[#64748B] mt-1 min-h-[88px]">{copy.description || plan.description}</p>

                  <div className="mt-4"><PriceLine plan={plan} billing={billing} /></div>

                  {/* Prominent allowances — shown before secondary inclusions */}
                  <div className="mt-5 space-y-2.5">
                    {copy.liveSurvey && (
                      <AllowanceBox
                        icon={Radio}
                        main={copy.liveSurvey.main}
                        sub={copy.liveSurvey.sub}
                        testid={`live-survey-${plan.plan_id}`}
                      />
                    )}
                    {copy.questions && (
                      <AllowanceBox
                        icon={ListChecks}
                        main={copy.questions.main}
                        sub={copy.questions.sub}
                        testid={`question-limit-${plan.plan_id}`}
                      />
                    )}
                  </div>

                  <ul className="mt-5 space-y-2.5 flex-1" data-testid={`features-${plan.plan_id}`}>
                    {(copy.inclusions || plan.features || []).map((f, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-2 text-sm ${
                          f === 'Unlimited responses' ? 'font-semibold text-[#0F172A]' : 'text-[#0F172A]'
                        }`}
                      >
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#22C55E]" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => startCheckout(plan)}
                    disabled={checkoutBusy === plan.plan_id}
                    className={`mt-6 w-full rounded-full font-semibold transition-colors ${
                      featured ? 'bg-[#1A3E6F] hover:bg-[#122c50] text-white' : 'bg-[#0F172A] hover:bg-[#1E293B] text-white'
                    }`}
                    data-testid={`cta-${plan.plan_id}`}
                  >
                    {checkoutBusy === plan.plan_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Choose Your Plan'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Shared live-survey explanation */}
      <section className="max-w-4xl mx-auto px-6 pb-10">
        <p className="text-sm text-[#64748B] text-center" data-testid="live-survey-note">
          A live survey is open to receive responses. Each product subscription includes its live survey allowance across all your subscribed branches. Adding branches does not increase the live survey allowance.
        </p>
      </section>

      {/* Additional survey panel */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div
          className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
          data-testid="additional-survey-panel"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1A3E6F] text-white">
              <PlusCircle className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-lg font-bold font-['Montserrat'] text-[#0F172A]">
                Need more surveys live at the same time?
              </h3>
              <p className="text-sm text-[#64748B] mt-1 max-w-xl">
                Add an extra live survey for your organization. The additional survey fee is charged once for the organization, not separately for every branch.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-[#0F172A] font-['Montserrat']">US$150</span>
              <span className="text-sm text-[#64748B]">per additional survey / year</span>
            </div>
            <a
              href={CONTACT_HREF}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#1A3E6F] text-white text-sm font-semibold hover:bg-[#122c50] transition-colors"
              data-testid="additional-survey-contact-btn"
            >
              Contact us
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold font-['Montserrat'] text-[#0F172A] text-center mb-6">
          Frequently Asked Questions
        </h2>
        <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-2" data-testid="faq-accordion">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-[#E2E8F0] rounded-lg px-4">
              <AccordionTrigger className="text-sm font-semibold text-[#0F172A] py-3 text-left">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#64748B] pb-3">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* BOTTOM CTA BANNER */}
      <section className="bg-[#1A3E6F] text-white" data-testid="cta-banner">
        <div className="max-w-5xl mx-auto px-6 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-['Montserrat']">
            Need help choosing?
          </h2>
          <p className="mt-3 text-base text-white/80 max-w-2xl mx-auto">
            Tell us what you want to understand and how many branches you have. Our team can help you choose the right product.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={CONTACT_HREF}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-[#1A3E6F] font-semibold hover:bg-[#F8FAFC] transition-colors"
              data-testid="contact-us-btn"
            >
              Contact us
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 text-center text-xs text-[#64748B] space-y-3">
          <img
            src={SATISFIND_LOGO}
            alt="satisFIND"
            style={{ width: 140, height: 'auto' }}
            className="mx-auto"
            data-testid="pricing-footer-logo"
          />
          <p>NPS&reg; is a registered trademark of Bain &amp; Company, Inc. and Fred Reichheld.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="footer-legal-links">
            <a
              href="https://satisfind.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#0F172A] transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </a>
            <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
            <a
              href="https://satisfind.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#0F172A] transition-colors"
              data-testid="footer-terms-link"
            >
              Terms &amp; Conditions
            </a>
          </div>
          <p data-testid="pricing-footer-copy">© 2026 satisFIND&reg; | Delivering Trust Since 2005</p>
        </div>
      </footer>

      <SignupModal open={signupOpen} onOpenChange={setSignupOpen} planId={signupPlanId} />
      <OrderReviewModal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        product={reviewProduct}
        plan={reviewPlan}
        initialCycle={billing}
      />
    </div>
  );
}
